import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useLocation } from '@reach/router'
import { useStaticQuery, graphql } from 'gatsby'
import Popup from '@src/components/site/Popup/Popup'
import styles from '@src/components/site/Popup/Popup.module.scss'

// Storage key for dismissed popups
const DISMISSED_POPUPS_KEY = 'dismissed_popups'

/**
 * Component to render Prismic rich text content with proper formatting
 * @param {Array} richTextItems - Array of rich text items from Prismic
 */
const RichTextRenderer = ({ richTextItems }) => {
  if (!richTextItems || !richTextItems.length) return null

  const result = []
  let currentList = null
  let currentListType = null

  // Helper function to render text with spans (bold, italic, etc.)
  const renderTextWithSpans = (text, spans, itemIndex) => {
    if (!spans || spans.length === 0) return text

    // Sort spans by start position
    const sortedSpans = [...spans].sort((a, b) => a.start - b.start)

    // Build text with spans
    let lastIndex = 0
    const parts = []

    sortedSpans.forEach((span, i) => {
      // Add text before the span
      if (span.start > lastIndex) {
        parts.push(text.substring(lastIndex, span.start))
      }

      // Add the span content with appropriate formatting
      const spanContent = text.substring(span.start, span.end)
      switch (span.type) {
        case 'strong':
          parts.push(
            <strong key={`span-${itemIndex}-${i}`}>{spanContent}</strong>
          )
          break
        case 'em':
          parts.push(<em key={`span-${itemIndex}-${i}`}>{spanContent}</em>)
          break
        case 'hyperlink':
          parts.push(
            <a
              key={`span-${itemIndex}-${i}`}
              href={span.data?.url}
              rel='noopener noreferrer'
              target='_blank'
            >
              {spanContent}
            </a>
          )
          break
        default:
          parts.push(spanContent)
      }

      lastIndex = span.end
    })

    // Add any remaining text after the last span
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex))
    }

    return parts
  }

  richTextItems.forEach((item, index) => {
    // Handle different types of rich text elements
    if (item.type === 'paragraph') {
      // Close any open list before adding a paragraph
      if (currentList) {
        result.push(
          React.cloneElement(currentList[0], {}, currentList.slice(1))
        )
        currentList = null
        currentListType = null
      }
      result.push(
        <p key={`p-${index}`}>
          {renderTextWithSpans(item.text, item.spans, index)}
        </p>
      )
    } else if (item.type === 'list-item' || item.type === 'o-list-item') {
      // Handle list items
      const listType = item.type === 'o-list-item' ? 'ol' : 'ul'

      // If we're already in a list of a different type, close it
      if (currentListType && currentListType !== listType) {
        result.push(
          React.cloneElement(currentList[0], {}, currentList.slice(1))
        )
        currentList = null
      }

      // Start a new list if needed
      if (!currentList) {
        currentList = [
          listType === 'ol' ? (
            <ol key={`list-${index}`}></ol>
          ) : (
            <ul key={`list-${index}`}></ul>
          )
        ]
        currentListType = listType
      }

      // Add the list item to the current list
      currentList.push(
        <li key={`li-${index}`}>
          {renderTextWithSpans(item.text, item.spans, index)}
        </li>
      )

      // If this is the last item, close the list
      if (index === richTextItems.length - 1) {
        result.push(
          React.cloneElement(currentList[0], {}, currentList.slice(1))
        )
      }
    } else {
      // Handle any other types
      if (currentList) {
        result.push(
          React.cloneElement(currentList[0], {}, currentList.slice(1))
        )
        currentList = null
        currentListType = null
      }
      result.push(
        <p key={`other-${index}`}>
          {renderTextWithSpans(item.text, item.spans, index)}
        </p>
      )
    }
  })

  // If there's still an open list, close it
  if (currentList) {
    result.push(React.cloneElement(currentList[0], {}, currentList.slice(1)))
  }

  return result
}

/**
 * Hook to manage popup visibility based on popup placements data and previous dismissals
 * @param {string} popupId - Optional override for popup ID (if not using data from query)
 * @param {boolean} showOnlyOnce - If true, popup will only show once per user (stored in localStorage)
 * @param {number} expirationDays - Number of days before a dismissed popup can be shown again (default: 30)
 */
const usePopup = (
  popupId = null,
  showOnlyOnce = false,
  expirationDays = 30
) => {
  // Query the popups data from Prismic with the simplified structure
  const data = useStaticQuery(graphql`
    {
      allPrismicPopups {
        edges {
          node {
            id
            data {
              popup_title
              popup_image {
                url
                dimensions {
                  width
                  height
                }
              }
              popup_richtext {
                type
                text
                spans {
                  start
                  end
                  type
                }
                direction
              }
              popup_link_label
              popup_link {
                link_type
                type
                ... on PrismicPopupsDataPopup_link {
                  url
                  uid
                  target
                }
              }
              popup_pathnames
            }
          }
        }
      }
    }
  `)

  // Use two state variables for better control
  const [show, setShow] = useState(false)
  const [forcedShow, setForcedShow] = useState(false) // This will force the popup to show
  const [activePopup, setActivePopup] = useState(null)
  const [manuallyDismissed, setManuallyDismissed] = useState(false) // Track manual dismissal
  const location = useLocation()

  // Combine the two show states
  const isPopupVisible = show || forcedShow

  // Process the GraphQL data to get popups - memoized to prevent reprocessing on every render
  const popupPlacements = useMemo(() => {
    const popupEdges = data?.allPrismicPopups?.edges || []

    // Map the popup data to our expected format
    return popupEdges.map(({ node }) => ({
      id: node.id, // Include the unique ID from Prismic
      popup: {
        data: {
          popup_title: node.data?.popup_title || '',
          popup_image: node.data?.popup_image || null,
          popup_richtext: node.data?.popup_richtext || [],
          popup_link_label: node.data?.popup_link_label || '',
          popup_link: node.data?.popup_link || null
        }
      },
      popup_pathnames: node.data?.popup_pathnames || ''
    }))
  }, [data])

  // Helper function to normalize paths for consistent matching
  const normalizePath = useCallback(path => {
    if (!path) return ''
    // Ensure path starts with / and ends with /
    let normalized = path.startsWith('/') ? path : `/${path}`
    normalized = normalized.endsWith('/') ? normalized : `${normalized}/`
    return normalized
  }, [])

  // Check if a pattern matches a path
  const isPatternMatch = useCallback(
    (pattern, path) => {
      if (!pattern || !path) return false

      const normalizedPath = normalizePath(path)
      let normalizedPattern = normalizePath(pattern)

      // Case 1: Exact match (with normalized paths)
      if (normalizedPath === normalizedPattern) return true

      // Case 2: Wildcard match for all paths under a directory (/path/*)
      if (pattern.endsWith('/*')) {
        const basePath = normalizePath(pattern.slice(0, -2))
        return (
          normalizedPath.startsWith(basePath) && normalizedPath !== basePath
        )
      }

      // Case 3: Wildcard match for directory and all paths under it (/path*)
      if (pattern.endsWith('*') && !pattern.endsWith('/*')) {
        const basePath = normalizePath(pattern.slice(0, -1))
        return normalizedPath.startsWith(basePath)
      }

      return false
    },
    [normalizePath]
  )

  // Find matching popup based on current pathname
  const findMatchingPopup = useCallback(() => {
    if (!location.pathname) return null

    // Find the first popup that matches the current pathname
    return (
      popupPlacements.find(placement => {
        if (!placement.popup_pathnames) return false

        // Split patterns and check if any match
        const patterns = placement.popup_pathnames
          .split(',')
          .map(p => p.trim())
          .filter(Boolean)

        return patterns.some(pattern =>
          isPatternMatch(pattern, location.pathname)
        )
      }) || null
    )
  }, [location.pathname, popupPlacements, isPatternMatch])

  // Check if popup has been dismissed before
  const hasBeenDismissed = useCallback(
    (id = null) => {
      // Use provided ID, custom popupId from props, or the ID from the active popup
      const popupIdToCheck = id || popupId || activePopup?.id
      if (!popupIdToCheck || typeof window === 'undefined') return false

      try {
        const dismissedPopups = JSON.parse(
          localStorage.getItem(DISMISSED_POPUPS_KEY) || '{}'
        )

        if (dismissedPopups[popupIdToCheck]) {
          // Check if the dismissal has expired
          const dismissedTime = new Date(dismissedPopups[popupIdToCheck])
          const now = new Date()
          const daysSinceDismissal = Math.floor(
            (now - dismissedTime) / (1000 * 60 * 60 * 24)
          )

          return showOnlyOnce || daysSinceDismissal < expirationDays
        }

        return false
      } catch (error) {
        console.error('Error checking dismissed popups:', error)
        return false
      }
    },
    [popupId, showOnlyOnce, expirationDays]
  )

  // Store dismissal in localStorage immediately when popup is closed
  const storeDismissal = useCallback(() => {
    // Use the actual Prismic ID for the popup
    const currentPopupId = popupId || activePopup?.id || 'default-popup-id'

    if (typeof window === 'undefined') return false

    try {
      const dismissedPopups = JSON.parse(
        localStorage.getItem(DISMISSED_POPUPS_KEY) || '{}'
      )
      dismissedPopups[currentPopupId] = new Date().toISOString()
      localStorage.setItem(
        DISMISSED_POPUPS_KEY,
        JSON.stringify(dismissedPopups)
      )
      console.log(`Stored dismissal for popup: ${currentPopupId}`)
      return true
    } catch (error) {
      console.error('Error storing dismissal:', error)
      return false
    }
  }, [popupId, activePopup])

  // Open/close popup methods
  const openPopup = useCallback(() => {
    console.log('Explicitly opening popup')
    setShow(true)
    setForcedShow(true) // Also set the forced show state
    console.log('Setting both show states to true')
  }, [])

  // Close popup and optionally mark as dismissed
  const closePopup = useCallback(
    (markAsDismissed = true) => {
      setShow(false)
      setForcedShow(false) // Also reset the forced show state

      if (markAsDismissed) {
        setManuallyDismissed(true) // Set the manual dismissal flag
        storeDismissal() // Store dismissal in localStorage immediately
      }
    },
    [storeDismissal]
  )

  // Handle open/close changes
  const handleOpenChange = useCallback(
    open => {
      if (open) {
        setShow(true)
      } else {
        closePopup(true) // Mark as dismissed when closed via the dialog
      }
    },
    [closePopup]
  )

  // Check for matching popup on location change
  // Memoize the current popup ID to prevent recalculations
  const currentPopupId = React.useMemo(() => {
    const matchingPopup = findMatchingPopup()
    if (!matchingPopup) return null

    console.log('matchingPopup', matchingPopup)

    // Use the Prismic ID directly
    return popupId || matchingPopup.id || 'default-popup-id'
  }, [findMatchingPopup, popupId])

  // Effect to handle popup visibility based on path changes
  useEffect(() => {
    const matchingPopup = findMatchingPopup()

    if (matchingPopup) {
      // Explicitly set the active popup first
      setActivePopup(matchingPopup)

      const isDismissed = currentPopupId
        ? hasBeenDismissed(currentPopupId)
        : false

      // Don't show if manually dismissed during this session or previously dismissed in localStorage
      if (!isDismissed && !manuallyDismissed) {
        setShow(true)
        setForcedShow(true)
      } else {
        // Ensure popup stays closed
        setShow(false)
        setForcedShow(false)
      }
    } else if (show) {
      closePopup(false) // Close without marking as dismissed if no matching popup
    }
  }, [
    location.pathname,
    findMatchingPopup,
    currentPopupId,
    hasBeenDismissed,
    manuallyDismissed,
    show,
    closePopup
  ])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setShow(false)
    }
  }, [])

  // Create a PopupComponent function that can be directly used in templates
  const PopupComponent = useMemo(() => {
    // Return a component function, not an element
    return function PopupComponentFunction() {
      if (!activePopup) {
        // Return an empty popup that's never shown
        return <Popup show={false} onOpenChange={handleOpenChange} />
      }

      return (
        <Popup
          show={isPopupVisible}
          onOpenChange={handleOpenChange}
          title={activePopup?.popup?.data?.popup_title || ''}
          className={styles.popup}
        >
          {activePopup.popup?.data?.popup_image?.url && (
            <img
              src={activePopup.popup.data.popup_image.url}
              alt={activePopup.popup?.data?.popup_title || 'Popup image'}
              className={styles.image}
              width={activePopup.popup.data.popup_image.dimensions?.width}
              height={activePopup.popup.data.popup_image.dimensions?.height}
              loading='lazy'
            />
          )}

          {/* Process the rich text content from Prismic */}
          {activePopup.popup?.data?.popup_richtext && (
            <div className={styles.richTextContent}>
              <RichTextRenderer
                richTextItems={activePopup.popup.data.popup_richtext}
              />
            </div>
          )}

          {activePopup.popup?.data?.popup_link && (
            <a
              href={
                // External link
                activePopup.popup.data.popup_link.link_type === 'Web'
                  ? activePopup.popup.data.popup_link.url
                  : // Internal document link
                  activePopup.popup.data.popup_link.link_type === 'Document'
                  ? `/${activePopup.popup.data.popup_link.uid}`
                  : '#'
              }
              {...(activePopup.popup.data.popup_link.link_type === 'Web' && {
                rel: 'noopener noreferrer'
              })}
              target={activePopup.popup.data.popup_link.target || '_self'}
              className={styles.link}
              aria-label={
                activePopup.popup.data.popup_link_label || 'Sjá nánar'
              }
            >
              {activePopup.popup.data.popup_link_label || 'Sjá nánar'}
            </a>
          )}
        </Popup>
      )
    }
  }, [isPopupVisible, activePopup, handleOpenChange])

  return {
    show: isPopupVisible, // Return the combined show state
    openPopup,
    closePopup,
    handleOpenChange,
    activePopup,
    hasMatchingPopup: !!activePopup,
    isDismissed: hasBeenDismissed(),
    PopupComponent,
    // Helper method to reset dismissal status (useful for testing)
    resetDismissalStatus: useCallback(() => {
      // Use the actual Prismic ID for the popup
      const currentPopupId = popupId || activePopup?.id

      if (currentPopupId && typeof window !== 'undefined') {
        try {
          const dismissedPopups = JSON.parse(
            localStorage.getItem(DISMISSED_POPUPS_KEY) || '{}'
          )
          delete dismissedPopups[currentPopupId]
          localStorage.setItem(
            DISMISSED_POPUPS_KEY,
            JSON.stringify(dismissedPopups)
          )
          return true
        } catch (error) {
          console.error('Error resetting dismissal status:', error)
          return false
        }
      }
      return false
    }, [popupId, activePopup]),

    // Helper to clear all dismissed popups (for testing)
    clearAllDismissedPopups: useCallback(() => {
      if (typeof window !== 'undefined') {
        try {
          localStorage.removeItem(DISMISSED_POPUPS_KEY)
          setManuallyDismissed(false) // Reset the manual dismissal flag
          return true
        } catch (error) {
          console.error('Error clearing dismissed popups:', error)
          return false
        }
      }
      return false
    }, [])
  }
}

export default usePopup
